"use strict";
/**
 * MusicFeatureService.ts
 * Implements:
 * 1. Profile Theme Song (MySpace / Discord Style Background Music)
 * 2. Musical Matchmaking (/match command) for DM chats
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.musicFeatureService = void 0;
const useStore_1 = require("../store/useStore");
class MusicFeatureService {
    constructor() {
        this.listeners = new Set();
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    notify() {
        this.listeners.forEach(l => l());
    }
    /**
     * Save a user's chosen profile theme song strictly for their userId
     */
    setProfileSong(userId, song) {
        if (typeof window === 'undefined' || !userId)
            return;
        localStorage.setItem(`meci_profile_song_${userId}`, JSON.stringify(song));
        this.pushToServer({ profile_song: song });
        this.notify();
    }
    pushToServer(payload) {
        try {
            fetch(`${useStore_1.API_BASE_URL}/users/music`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include',
            }).catch(() => { });
        }
        catch { }
    }
    pushNowPlaying(track) {
        this.pushToServer({ now_playing: track });
    }
    /**
     * Load another user's profile song / now playing from the server (per-user, not shared)
     */
    async fetchRemote(userId) {
        try {
            const res = await fetch(`${useStore_1.API_BASE_URL}/users/music?id=${encodeURIComponent(String(userId))}`, { credentials: 'include' });
            if (!res.ok)
                return null;
            const data = await res.json();
            this.syncFromRemote(userId, data.profile_song || null);
            return data;
        }
        catch {
            return null;
        }
    }
    /**
     * Remove a user's chosen profile theme song
     */
    removeProfileSong(userId) {
        if (typeof window === 'undefined' || !userId)
            return;
        localStorage.removeItem(`meci_profile_song_${userId}`);
        this.pushToServer({ profile_song: null });
        this.notify();
    }
    /**
     * Retrieve a user's chosen profile theme song strictly by userId
     */
    getProfileSong(userId) {
        if (typeof window === 'undefined' || !userId)
            return null;
        const raw = localStorage.getItem(`meci_profile_song_${userId}`);
        if (!raw)
            return null;
        try {
            return JSON.parse(raw);
        }
        catch {
            return null;
        }
    }
    /**
     * Sync remote profile song received over socket
     */
    syncFromRemote(userId, song) {
        if (typeof window === 'undefined' || !userId)
            return;
        if (song) {
            localStorage.setItem(`meci_profile_song_${userId}`, JSON.stringify(song));
        }
        else {
            localStorage.removeItem(`meci_profile_song_${userId}`);
        }
        this.notify();
    }
    /**
     * Compute musical compatibility between two users for the /match command
     */
    calculateMatch(currentUserId, currentUserName, targetUserId, targetUserName) {
        const currentSong = this.getProfileSong(currentUserId);
        const targetSong = this.getProfileSong(targetUserId);
        // Deterministic pseudo-random seed based on user IDs
        const combinedStr = [String(currentUserId), String(targetUserId)].sort().join('_');
        let hash = 0;
        for (let i = 0; i < combinedStr.length; i++) {
            hash = (hash << 5) - hash + combinedStr.charCodeAt(i);
            hash |= 0;
        }
        const seed = Math.abs(hash);
        // Score between 74% and 98%
        const baseScore = 74 + (seed % 24);
        const vibeLists = [
            ['Phonk', 'Drift Electro', 'Bass Boosted'],
            ['Deep House', 'Chillstep', 'Melodic Electronic'],
            ['Lofi Hip-Hop', 'Study Chill', 'Late Night Vibes'],
            ['Hardstyle', 'Rave Synth', 'Energy EDM'],
            ['Indie Rock', 'Alternative Wave', 'Post-Punk'],
            ['Anadolu Pop / Rock', 'Synthwave', 'Turkish Retro'],
        ];
        const sharedVibes = vibeLists[seed % vibeLists.length];
        let grade = 'Ateşli Uyum! 🔥';
        let commentary = 'Müzik frekanslarınız neredeyse aynı ritimde atıyor! Ortak kulaklık taksanız kavga çıkmaz.';
        if (baseScore >= 92) {
            grade = 'Kusursuz Ruh Eşi! ⚡💎';
            commentary = 'İnanılmaz bir müzikal harmoni! İkinizin de favori parçaları aynı dalga boyunda buluşuyor.';
        }
        else if (baseScore >= 84) {
            grade = 'Yüksek Sinerji! 🎧✨';
            commentary = 'Aynı playlisti saatlerce kesintisiz dinleyebilirsiniz. Birbirinize şarkı önerme vakti!';
        }
        else {
            grade = 'Tatlı Çelişki & Keşif! 🎶';
            commentary = 'Farklı ama birbirini tamamlayan zevkler! Yeni müzikler keşfetmek için harika bir ikili.';
        }
        return {
            score: baseScore,
            grade,
            sharedVibes,
            commentary,
            currentUserMusic: currentSong ? `${currentSong.name} - ${currentSong.artist}` : 'SoundCloud / Favoriler',
            targetUserMusic: targetSong ? `${targetSong.name} - ${targetSong.artist}` : 'Müzik Kitaplığı',
        };
    }
    /**
     * Formats the /match command output for sending into DM chat
     */
    formatMatchMessage(currentUserName, targetUserName, result) {
        return [
            `🎧 ═══════ MÜZİKAL EŞLEŞME RAPORU ═══════ 🎧`,
            `✨ @${currentUserName} & @${targetUserName}`,
            `⚡ Müzikal Zevk Uyumu: %${result.score} (${result.grade})`,
            `🔥 Ortak Müzik Damarı: ${result.sharedVibes.join(' • ')}`,
            `💬 Zevk Analizi: ${result.commentary}`,
            `🎵 Öneri: Birlikte dinlemek için bir ses odasına geçin veya şarkı paylaşın!`,
            `═════════════════════════════════════`,
        ].join('\n');
    }
}
exports.musicFeatureService = new MusicFeatureService();
